import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {
  Announcement,
  AnnouncementId,
  FreshAnnouncement,
} from "../models/Announcement";
import { OwnerId } from "../models/User";

const firestore = firebase.firestore;
const ANNOUNCEMENTS = "announcements";
const DISMISSALS = "announcementDismissals";

export class AnnouncementService {
  /** Live-observe ALL announcements (no audience filtering in v1). Returns
   *  an unsubscribe function. The caller is expected to filter by
   *  `expiresAt > Date.now()` client-side. */
  static observeAll(
    onUpdate: (announcements: Announcement[]) => void,
  ): () => void {
    const db = firestore();
    return db
      .collection(ANNOUNCEMENTS)
      .orderBy("createdAt", "desc")
      .onSnapshot((snap) => {
        const all: Announcement[] = snap.docs.map((d) => ({
          ...(d.data() as Announcement),
          id: d.id,
        }));
        onUpdate(all);
      });
  }

  /** Live-observe a user's dismissal records. */
  static observeDismissals(
    ownerId: OwnerId,
    onUpdate: (dismissedIds: Set<AnnouncementId>) => void,
  ): () => void {
    const db = firestore();
    return db
      .collection(DISMISSALS)
      .doc(ownerId)
      .collection("dismissed")
      .onSnapshot((snap) => {
        const ids = new Set<AnnouncementId>();
        snap.docs.forEach((d) => ids.add(d.id));
        onUpdate(ids);
      });
  }

  static create(announcement: FreshAnnouncement): Promise<AnnouncementId> {
    const db = firestore();
    return db
      .collection(ANNOUNCEMENTS)
      .add(announcement)
      .then((ref) => ref.id);
  }

  static update(
    id: AnnouncementId,
    patch: Partial<FreshAnnouncement>,
  ): Promise<void> {
    const db = firestore();
    return db
      .collection(ANNOUNCEMENTS)
      .doc(id)
      .set(patch, { merge: true })
      .then(() => undefined);
  }

  static remove(id: AnnouncementId): Promise<void> {
    const db = firestore();
    return db
      .collection(ANNOUNCEMENTS)
      .doc(id)
      .delete()
      .then(() => undefined);
  }

  static dismiss(
    ownerId: OwnerId,
    announcementId: AnnouncementId,
  ): Promise<void> {
    const db = firestore();
    return db
      .collection(DISMISSALS)
      .doc(ownerId)
      .collection("dismissed")
      .doc(announcementId)
      .set({ at: Date.now() })
      .then(() => undefined);
  }
}
